import { z } from 'zod'

import { apiClient } from '../client'
import { canopyPath } from '../routes'
import { displayScoresSchema } from './portfolio'

export const evidenceSchema = z
  .looseObject({
    id: z.string(),
    text: z.string().nullish(),
    evidenceText: z.string().nullish(),
    sourceType: z.string().nullish(),
    sourceUrl: z.string().nullish(),
    sourceDocumentId: z.string().nullish(),
    sourcePageRange: z.string().nullish(),
  })
  .transform(({ evidenceText, ...evidence }) => ({
    ...evidence,
    text: evidence.text ?? evidenceText ?? null,
  }))

export const dimensionAxisSchema = z.enum(['defensibility', 'readiness'])

export type DimensionAxis = z.infer<typeof dimensionAxisSchema>

export const dimensionSchema = z.looseObject({
  field: z.string(),
  axis: dimensionAxisSchema,
  label: z.string(),
  weightPercent: z.number(),

  value: z.number().nullish(),
  rubricLevelText: z.string().nullish(),
  evidence: z.array(evidenceSchema).default([]),
})

export type Dimension = z.infer<typeof dimensionSchema>

export const recommendationSchema = z.looseObject({
  priority: z.string().nullish(),
  action: z.string(),
  owner: z.string().nullish(),
  deadline: z.string().nullish(),
})

export type Recommendation = z.infer<typeof recommendationSchema>

export const scorecardCompanySchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  sector: z.string().nullish(),
  status: z.string().nullish(),
  arrBand: z.string().nullish(),
  fund: z.string().nullish(),
  serentFund: z.string().nullish(),
  onboardingStep: z.number().int().nullish(),
})

export const scorecardSnapshotSchema = z.looseObject({
  id: z.string(),
  snapshotDate: z.string().nullish(),
  scoredBy: z.string().nullish(),
  sourceKind: z.string().nullish(),
})

export const scorecardSchema = z.looseObject({
  company: scorecardCompanySchema,
  quarters: z.array(z.string()),
  quarter: z.string().nullish(),

  snapshot: scorecardSnapshotSchema.nullish(),
  scores: displayScoresSchema.nullable(),
  recommendations: z.array(recommendationSchema).default([]),

  dimensions: z.array(dimensionSchema),

  hasTrend: z.boolean(),
})

export type Scorecard = z.infer<typeof scorecardSchema>

export interface ScorecardOptions {
  quarter?: string | undefined
  signal?: AbortSignal | undefined
}

export function getCompanyScorecard(
  companyId: string,
  options: ScorecardOptions = {},
): Promise<Scorecard> {
  const { quarter, signal } = options

  return apiClient().get(canopyPath(`/companies/${encodeURIComponent(companyId)}/scorecard`), {
    schema: scorecardSchema,
    query: { quarter },
    ...(signal ? { signal } : {}),
  })
}

export const trendPointSchema = z.looseObject({
  key: z.string(),
  label: z.string(),

  sortKey: z.string(),
  compositeDefensibility: z.number().nullish(),
  compositeReadiness: z.number().nullish(),

  urgencyIndex: z.number().nullish(),
  portcoReadiness: z.number().nullish(),
  serentSource: z.boolean(),
  portcoSource: z.boolean(),
})

export type TrendPoint = z.infer<typeof trendPointSchema>

export const urgencyLevelSchema = z.looseObject({
  level: z.number().int(),
  label: z.string(),
  text: z.string().nullish(),
})

export const trendSchema = z.looseObject({
  points: z.array(trendPointSchema),

  urgencyLevels: z.array(urgencyLevelSchema).default([]),
})

export type Trend = z.infer<typeof trendSchema>

export function getCompanyTrend(companyId: string, signal?: AbortSignal): Promise<Trend> {
  return apiClient().get(canopyPath(`/companies/${encodeURIComponent(companyId)}/trend`), {
    schema: trendSchema,
    ...(signal ? { signal } : {}),
  })
}
